import React, { useState, useMemo, useCallback } from 'react';
import { formatDateTime as formatColombiaDateTime } from '../utils/dateFormat';
import {
  useDashboardStats,
  useSusceptibleInvoices,
  useDeletedInvoices,
  useElectronicInvoices,
  useInvoiceTypeStats,
  useInvoiceDeletion,
} from '../hooks/useBillingSystem';

const ITEMS_PER_PAGE = 30;

// Función para formatear fecha para input date
const formatDateForInput = (date: Date) => {
  return date.toISOString().split('T')[0];
};

// Obtener fecha hace 30 días
const getDefaultStartDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  date.setHours(0, 0, 0, 0);
  return date;
};

// Obtener fin del día de hoy
const getDefaultEndDate = () => {
  const date = new Date();
  date.setHours(23, 59, 59, 999);
  return date;
};

export const AdminPanel: React.FC = () => {
  // Estado para el rango de fechas (por defecto: últimos 30 días)
  const [startDate, setStartDate] = useState<Date>(getDefaultStartDate());
  const [endDate, setEndDate] = useState<Date>(getDefaultEndDate());

  // Rango de fechas para las consultas
  const dateRange = useMemo(() => ({
    startDate,
    endDate,
  }), [startDate, endDate]);

  const { stats, loading: statsLoading, refetch: refetchStats } = useDashboardStats({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });
  const {
    susceptibleInvoices,
    loading: susceptibleLoading,
    refetch: refetchSusceptible,
  } = useSusceptibleInvoices({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });
  const {
    deletedInvoices,
    loading: deletedLoading,
    refetch: refetchDeleted,
  } = useDeletedInvoices({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });
  const {
    electronicInvoices,
    loading: electronicLoading,
    refetch: refetchElectronic,
  } = useElectronicInvoices({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });
  const {
    stats: typeStats,
    refetch: refetchTypeStats,
  } = useInvoiceTypeStats({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });
  const { deleting, deleteInvoicesManually } = useInvoiceDeletion();

  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());
  const [deleteReason, setDeleteReason] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'electronic' | 'susceptible' | 'deleted'>('electronic');

  // Estado de paginación para cada tabla
  const [electronicPage, setElectronicPage] = useState(1);
  const [susceptiblePage, setSusceptiblePage] = useState(1);
  const [deletedPage, setDeletedPage] = useState(1);

  // Handler para cambio de fecha inicio
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = new Date(e.target.value + 'T00:00:00');
    setStartDate(newDate);
    setSelectedInvoices(new Set());
    resetPages();
  };

  // Handler para cambio de fecha fin
  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = new Date(e.target.value + 'T23:59:59');
    setEndDate(newDate);
    setSelectedInvoices(new Set());
    resetPages();
  };

  // Presets de rangos rápidos
  const setDatePreset = (preset: 'today' | 'week' | 'month' | 'all') => {
    const now = new Date();
    const end = new Date(now);
    end.setHours(23, 59, 59, 999);
    setEndDate(end);

    let start = new Date(now);
    switch (preset) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        break;
      case 'week':
        start.setDate(start.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        break;
      case 'month':
        start.setDate(start.getDate() - 30);
        start.setHours(0, 0, 0, 0);
        break;
      case 'all':
        start = new Date('2025-01-01T00:00:00');
        break;
    }
    setStartDate(start);
    setSelectedInvoices(new Set());
    resetPages();
  };

  // Formatear fecha para mostrar en resumen
  const formatDisplayDate = (date: Date) => {
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleSelectInvoice = (invoiceId: string) => {
    const newSelection = new Set(selectedInvoices);
    if (newSelection.has(invoiceId)) {
      newSelection.delete(invoiceId);
    } else {
      newSelection.add(invoiceId);
    }
    setSelectedInvoices(newSelection);
  };

  const handleSelectAll = () => {
    if (selectedInvoices.size === susceptibleInvoices.length) {
      setSelectedInvoices(new Set());
    } else {
      setSelectedInvoices(new Set(susceptibleInvoices.map((inv) => inv.id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedInvoices.size === 0) {
      alert('Seleccione al menos una factura');
      return;
    }

    if (!window.confirm(`¿Está seguro de eliminar ${selectedInvoices.size} facturas?`)) {
      return;
    }

    try {
      await deleteInvoicesManually(Array.from(selectedInvoices), deleteReason);
      setSelectedInvoices(new Set());
      setDeleteReason('');
      setShowDeleteModal(false);
      await Promise.all([refetchStats(), refetchSusceptible(), refetchDeleted()]);
      alert('Facturas eliminadas correctamente');
    } catch (error) {
      alert('Error al eliminar facturas');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: Date | string) => {
    return formatColombiaDateTime(date);
  };

  // Función para paginar datos
  const paginateData = useCallback(<T,>(data: T[], page: number): T[] => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    return data.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, []);

  // Calcular total de páginas
  const getTotalPages = useCallback((totalItems: number): number => {
    return Math.ceil(totalItems / ITEMS_PER_PAGE);
  }, []);

  // Datos paginados
  const paginatedElectronic = useMemo(() =>
    paginateData(electronicInvoices, electronicPage),
    [electronicInvoices, electronicPage, paginateData]
  );

  const paginatedSusceptible = useMemo(() =>
    paginateData(susceptibleInvoices, susceptiblePage),
    [susceptibleInvoices, susceptiblePage, paginateData]
  );

  const paginatedDeleted = useMemo(() =>
    paginateData(deletedInvoices, deletedPage),
    [deletedInvoices, deletedPage, paginateData]
  );

  // Totales de páginas
  const electronicTotalPages = getTotalPages(electronicInvoices.length);
  const susceptibleTotalPages = getTotalPages(susceptibleInvoices.length);
  const deletedTotalPages = getTotalPages(deletedInvoices.length);

  // Componente de paginación reutilizable
  const PaginationControls: React.FC<{
    currentPage: number;
    totalPages: number;
    totalItems: number;
    onPageChange: (page: number) => void;
    position?: 'top' | 'bottom';
  }> = ({ currentPage, totalPages, totalItems, onPageChange, position = 'bottom' }) => {
    if (totalItems <= ITEMS_PER_PAGE) return null;

    const startItem = (currentPage - 1) * ITEMS_PER_PAGE + 1;
    const endItem = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);

    return (
      <div className={`pagination-controls ${position === 'top' ? 'pagination-top' : 'pagination-bottom'}`}>
        <button
          className="btn-pagination"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          ← Anterior
        </button>
        <span className="pagination-info">
          Página {currentPage} de {totalPages} • Mostrando {startItem}-{endItem} de {totalItems}
        </span>
        <button
          className="btn-pagination"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Siguiente →
        </button>
      </div>
    );
  };

  // Reset de página al cambiar fechas
  const resetPages = useCallback(() => {
    setElectronicPage(1);
    setSusceptiblePage(1);
    setDeletedPage(1);
  }, []);

  if (statsLoading) {
    return <div className="loading">Cargando estadísticas...</div>;
  }

  return (
    <div className="admin-panel">
      <h2>Panel de Revisión Administrativa</h2>

      {/* Date Range Selector */}
      <div className="date-selector-container">
        <div className="date-range-selector">
          <div className="date-range-inputs">
            <div className="date-input-group">
              <label>Desde:</label>
              <input
                type="date"
                value={formatDateForInput(startDate)}
                onChange={handleStartDateChange}
                className="date-input"
              />
            </div>
            <span className="date-separator">→</span>
            <div className="date-input-group">
              <label>Hasta:</label>
              <input
                type="date"
                value={formatDateForInput(endDate)}
                onChange={handleEndDateChange}
                className="date-input"
              />
            </div>
          </div>

          <div className="date-presets">
            <button
              className="preset-btn"
              onClick={() => setDatePreset('today')}
              title="Ver solo facturas de hoy"
            >
              Hoy
            </button>
            <button
              className="preset-btn"
              onClick={() => setDatePreset('week')}
              title="Últimos 7 días"
            >
              7 días
            </button>
            <button
              className="preset-btn"
              onClick={() => setDatePreset('month')}
              title="Últimos 30 días"
            >
              30 días
            </button>
            <button
              className="preset-btn preset-all"
              onClick={() => setDatePreset('all')}
              title="Ver todas las facturas"
            >
              Todas
            </button>
          </div>

          <div className="date-range-summary">
            <span>{formatDisplayDate(startDate)} - {formatDisplayDate(endDate)}</span>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Facturas Totales</h3>
            <p className="stat-value">{stats.totalInvoices}</p>
            <p className="stat-label">{formatCurrency(stats.totalAmount)}</p>
          </div>

          <div className="stat-card susceptible">
            <h3>Susceptibles de Eliminación</h3>
            <p className="stat-value">{stats.susceptibleInvoices}</p>
            <p className="stat-label">{formatCurrency(stats.susceptibleAmount)}</p>
          </div>

          <div className="stat-card deleted">
            <h3>Facturas Eliminadas</h3>
            <p className="stat-value">{stats.deletedInvoices}</p>
            <p className="stat-label">{formatCurrency(stats.deletedAmount)}</p>
          </div>

          <div className="stat-card percentage">
            <h3>Porcentaje Eliminado</h3>
            <p className="stat-value">{stats.deletionPercentage.toFixed(2)}%</p>
            <p className="stat-label">Del monto total</p>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="action-bar">
        <button
          className="btn-primary"
          onClick={() => setShowDeleteModal(true)}
          disabled={selectedInvoices.size === 0 || deleting}
        >
          Eliminar Seleccionadas ({selectedInvoices.size})
        </button>
        <button
          className="btn-secondary"
          onClick={() => {
            refetchStats();
            refetchSusceptible();
            refetchDeleted();
            refetchElectronic();
            refetchTypeStats();
          }}
        >
          Actualizar
        </button>
      </div>

      <div className="info-box">
        <p>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display: 'inline', verticalAlign: 'middle', marginRight: '6px'}}>
            <path d="M9 18h6"/>
            <path d="M10 22h4"/>
            <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/>
          </svg>
          <strong>Nota:</strong> Para ejecutar la eliminación según la configuración establecida, usa el panel de "Autorizar Eliminación"
        </p>
      </div>

      {/* Invoice Type Stats */}
      {typeStats && (
        <div className="stats-grid invoice-type-stats">
          <div className="stat-card electronic">
            <h3>Facturas Electrónicas</h3>
            <p className="stat-value">{typeStats.electronic.count}</p>
            <p className="stat-label">{formatCurrency(typeStats.electronic.amount)}</p>
            <p className="stat-badge">No modificables</p>
          </div>

          <div className="stat-card susceptible">
            <h3>Facturas Susceptibles</h3>
            <p className="stat-value">{typeStats.susceptible.count}</p>
            <p className="stat-label">{formatCurrency(typeStats.susceptible.amount)}</p>
            <p className="stat-badge">Modificables</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'electronic' ? 'active' : ''}`}
          onClick={() => setActiveTab('electronic')}
        >
          Facturas Electrónicas ({electronicInvoices.length})
        </button>
        <button
          className={`tab ${activeTab === 'susceptible' ? 'active' : ''}`}
          onClick={() => setActiveTab('susceptible')}
        >
          Facturas Susceptibles ({susceptibleInvoices.length})
        </button>
        <button
          className={`tab ${activeTab === 'deleted' ? 'active' : ''}`}
          onClick={() => setActiveTab('deleted')}
        >
          Facturas Eliminadas ({deletedInvoices.length})
        </button>
      </div>

      {/* Electronic Invoices Table */}
      {activeTab === 'electronic' && (
        <div className="table-container">
          {electronicLoading ? (
            <div className="loading">Cargando facturas electrónicas...</div>
          ) : electronicInvoices.length === 0 ? (
            <div className="empty-state">No hay facturas electrónicas para esta fecha</div>
          ) : (
            <>
              <div className="table-info electronic-info">
                <span className="info-badge">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                    <path d="M12 16v-4m0-4h.01M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Estas facturas están transmitidas a la DIAN y NO pueden ser modificadas ni eliminadas
                </span>
              </div>
              <PaginationControls
                currentPage={electronicPage}
                totalPages={electronicTotalPages}
                totalItems={electronicInvoices.length}
                onPageChange={setElectronicPage}
                position="top"
              />
              <table className="invoice-table">
                <thead>
                  <tr>
                    <th>Número</th>
                    <th>Cliente</th>
                    <th>Documento</th>
                    <th>Subtotal</th>
                    <th>IVA</th>
                    <th>Total</th>
                    <th>Fecha</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedElectronic.map((invoice) => (
                    <tr key={invoice.id} className="electronic-row">
                      <td className="invoice-number">{invoice.invoiceNumber}</td>
                      <td>{invoice.customerName}</td>
                      <td>{invoice.customerDocument}</td>
                      <td>{formatCurrency(invoice.amount)}</td>
                      <td>{formatCurrency(invoice.tax)}</td>
                      <td className="total">{formatCurrency(invoice.total)}</td>
                      <td>{formatDate(invoice.createdAt)}</td>
                      <td>
                        <span className="status-badge dian">DIAN</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <PaginationControls
                currentPage={electronicPage}
                totalPages={electronicTotalPages}
                totalItems={electronicInvoices.length}
                onPageChange={setElectronicPage}
                position="bottom"
              />
            </>
          )}
        </div>
      )}

      {/* Susceptible Invoices Table */}
      {activeTab === 'susceptible' && (
        <div className="table-container">
          {susceptibleLoading ? (
            <div className="loading">Cargando facturas...</div>
          ) : susceptibleInvoices.length === 0 ? (
            <div className="empty-state">No hay facturas susceptibles de eliminación para esta fecha</div>
          ) : (
            <>
              <div className="table-actions">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={
                      selectedInvoices.size === susceptibleInvoices.length &&
                      susceptibleInvoices.length > 0
                    }
                    onChange={handleSelectAll}
                  />
                  <span>Seleccionar todas</span>
                </label>
              </div>
              <PaginationControls
                currentPage={susceptiblePage}
                totalPages={susceptibleTotalPages}
                totalItems={susceptibleInvoices.length}
                onPageChange={setSusceptiblePage}
                position="top"
              />
              <table className="invoice-table">
                <thead>
                  <tr>
                    <th>Seleccionar</th>
                    <th>Número</th>
                    <th>Cliente</th>
                    <th>Documento</th>
                    <th>Subtotal</th>
                    <th>IVA</th>
                    <th>Total</th>
                    <th>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSusceptible.map((invoice) => (
                    <tr key={invoice.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedInvoices.has(invoice.id)}
                          onChange={() => handleSelectInvoice(invoice.id)}
                        />
                      </td>
                      <td className="invoice-number">{invoice.invoiceNumber}</td>
                      <td>{invoice.customerName}</td>
                      <td>{invoice.customerDocument}</td>
                      <td>{formatCurrency(invoice.amount)}</td>
                      <td>{formatCurrency(invoice.tax)}</td>
                      <td className="total">{formatCurrency(invoice.total)}</td>
                      <td>{formatDate(invoice.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <PaginationControls
                currentPage={susceptiblePage}
                totalPages={susceptibleTotalPages}
                totalItems={susceptibleInvoices.length}
                onPageChange={setSusceptiblePage}
                position="bottom"
              />
            </>
          )}
        </div>
      )}

      {/* Deleted Invoices Table */}
      {activeTab === 'deleted' && (
        <div className="table-container">
          {deletedLoading ? (
            <div className="loading">Cargando facturas eliminadas...</div>
          ) : deletedInvoices.length === 0 ? (
            <div className="empty-state">No hay facturas eliminadas para esta fecha</div>
          ) : (
            <>
              <PaginationControls
                currentPage={deletedPage}
                totalPages={deletedTotalPages}
                totalItems={deletedInvoices.length}
                onPageChange={setDeletedPage}
                position="top"
              />
              <table className="invoice-table">
                <thead>
                  <tr>
                    <th>Número</th>
                    <th>Cliente</th>
                    <th>Documento</th>
                    <th>Total</th>
                    <th>Fecha Creación</th>
                    <th>Fecha Eliminación</th>
                    <th>Eliminado Por</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedDeleted.map((invoice) => (
                    <tr key={invoice.id}>
                      <td className="invoice-number">{invoice.invoiceNumber}</td>
                      <td>{invoice.customerName}</td>
                      <td>{invoice.customerDocument}</td>
                      <td className="total">{formatCurrency(invoice.total)}</td>
                      <td>{formatDate(invoice.createdAt)}</td>
                      <td>{invoice.deletedAt ? formatDate(invoice.deletedAt) : '-'}</td>
                      <td>{invoice.deletedBy || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <PaginationControls
                currentPage={deletedPage}
                totalPages={deletedTotalPages}
                totalItems={deletedInvoices.length}
                onPageChange={setDeletedPage}
                position="bottom"
              />
            </>
          )}
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Eliminar Facturas</h3>
            <p>
              Se eliminarán <strong>{selectedInvoices.size}</strong> facturas seleccionadas.
            </p>
            <div className="form-group">
              <label>Motivo de eliminación (opcional)</label>
              <textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="Ingrese el motivo de la eliminación..."
                rows={4}
              />
            </div>
            <div className="modal-actions">
              <button
                className="btn-danger"
                onClick={handleDeleteSelected}
                disabled={deleting}
              >
                {deleting ? 'Eliminando...' : 'Confirmar Eliminación'}
              </button>
              <button
                className="btn-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
